import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { FailReportDataUpdateItem } from '@/model/FailReport/FailReport';
import { PRODUCT_TYPES, ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';

/** Elemento simple asociado a la falla del ticket. */
export interface TicketFailItem {
  key: string;
  product_type: PRODUCT_TYPES;
  product: number;
  name: string;
  serial_number?: string;
  comment?: string;
}

/**
 * Hook que encapsula la lógica de la tabla inline de elementos involucrados en la falla.
 * Maneja el estado de los items, la fila de creación y la sincronización con el form padre.
 */
const useTicketFormFailInfo = (initialItems?: TicketFailItem[]) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [items, setItems] = useState<TicketFailItem[]>(initialItems ?? []);
  // Si los items iniciales (reporte automático ya existente) llegan después del primer
  // render, los aplicamos una sola vez sin pisar lo que el usuario ya haya tocado.
  const hasDoneInitialSync = useRef(Boolean(initialItems?.length));

  useEffect(() => {
    if (!hasDoneInitialSync.current && initialItems && initialItems.length > 0) {
      setItems(initialItems);
      hasDoneInitialSync.current = true;
    }
  }, [initialItems]);
  // Estado para la fila inline de nuevo elemento
  const [newProductType, setNewProductType] = useState<PRODUCT_TYPES | undefined>(undefined);
  const [newProduct, setNewProduct] = useState<number | undefined>(undefined);
  const [newProductLabel, setNewProductLabel] = useState('');
  const [newSerialNumber, setNewSerialNumber] = useState<string | undefined>(undefined);
  const [newComments, setNewComments] = useState('');
  // Indica si el tipo seleccionado requiere número de serie
  const needsSerialNumber =
    newProductType === PRODUCT_TYPES.UNIT || newProductType === PRODUCT_TYPES.DEVICE;
  // Indica si alguno de los items cargados tiene número de serie (para mostrar columna)
  const showSerialColumn = useMemo(
    () => needsSerialNumber || items.some(i => i.serial_number),
    [items, needsSerialNumber],
  );
  // Select de productos según el tipo seleccionado
  const { selectProps: productsSelectProps } = useSelectScrollSearch({
    resource: 'elements',
    optionLabel: (item: any) => item.name,
    optionValue: (item: any) => item.id,
    queryOptions: { enabled: newProductType !== undefined },
    filters: [{ value: newProductType, operator: 'eq' as const, field: 'type' }],
  });
  // Select de números de serie (solo para unidades y dispositivos)
  const { selectProps: serialNumbersSelectProps } = useSelectScrollSearch({
    resource: 'elements',
    optionLabel: (item: any) => item.name,
    optionValue: (item: any) =>
      newProductType === PRODUCT_TYPES.UNIT ? item.serial_number_box : item.id,
    queryOptions: {
      enabled: needsSerialNumber,
      queryKey: ['elements', { type: newProductType, serializable: true }],
    },
    filters: [
      { value: newProductType, operator: 'eq' as const, field: 'type' },
      { value: true, operator: 'eq' as const, field: 'serializable' },
    ],
  });
  // Opciones de tipo de producto
  const productTypeOptions = useMemo(
    () => Object.values(ProductTypeStrings).map((v, index) => ({ label: t(v), value: index })),
    [t],
  );
  // Sincroniza los items al campo oculto del form, mapeados al shape que espera
  // `fail_report_data_update.fail_report_items` (product/device/unidad según el tipo elegido).
  const failReportItemsPayload = useMemo<FailReportDataUpdateItem[]>(
    () =>
      items.map(({ product_type, product, comment }) => ({
        product: product_type === PRODUCT_TYPES.PRODUCT ? product : null,
        device: product_type === PRODUCT_TYPES.DEVICE ? product : null,
        unidad: product_type === PRODUCT_TYPES.UNIT ? product : null,
        comment: comment || undefined,
      })),
    [items],
  );

  useEffect(() => {
    form?.setFieldValue('fail_report_items', failReportItemsPayload);
  }, [failReportItemsPayload, form]);

  const resetNewRow = useCallback(() => {
    setNewProductType(undefined);
    setNewProduct(undefined);
    setNewProductLabel('');
    setNewSerialNumber(undefined);
    setNewComments('');
  }, []);
  const handleAdd = useCallback(() => {
    if (newProduct === undefined || newProductType === undefined) return;
    const item: TicketFailItem = {
      key: Date.now().toString(),
      product_type: newProductType,
      product: newProduct,
      name: newProductLabel,
      serial_number: newSerialNumber,
      comment: newComments || undefined,
    };

    setItems(prev => [...prev, item]);
    resetNewRow();
  }, [newProduct, newProductType, newProductLabel, newSerialNumber, newComments, resetNewRow]);
  const handleDelete = useCallback((key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);
  const handleProductTypeChange = useCallback((value: PRODUCT_TYPES | undefined) => {
    setNewProductType(value);
    setNewProduct(undefined);
    setNewProductLabel('');
    setNewSerialNumber(undefined);
  }, []);
  const handleProductChange = useCallback((value: number | undefined, opt: any) => {
    setNewProduct(value);
    if (!Array.isArray(opt)) {
      setNewProductLabel((opt?.label as string) ?? '');
    }
  }, []);
  const handleSerialNumberChange = useCallback((value: string | undefined, opt: any) => {
    setNewSerialNumber(!Array.isArray(opt) ? ((opt?.label as string) ?? value) : value);
  }, []);

  return {
    items,
    newProductType,
    newProduct,
    newSerialNumber,
    newComments,
    setNewComments,
    needsSerialNumber,
    showSerialColumn,
    productsSelectProps: productsSelectProps,
    serialNumbersSelectProps: serialNumbersSelectProps,
    productTypeOptions,
    handleAdd,
    handleDelete,
    handleProductTypeChange,
    handleProductChange,
    handleSerialNumberChange,
  };
};

export default useTicketFormFailInfo;
